import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';

const VendorContext = createContext(null);

export const VendorProvider = ({ children }) => {
  const [selectedVendorId, setSelectedVendorIdState] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const setSelectedVendorId = useCallback((vendorId) => {
    const normalized = vendorId == null || vendorId === 'public' ? null : Number(vendorId);
    const next = normalized != null && Number.isFinite(normalized) ? normalized : null;
    setSelectedVendorIdState(next);
  }, []);

  const loadVendors = useCallback(async (keyword = '') => {
    setVendorsLoading(true);
    try {
      const res = await api.getVendors({
        keyword: keyword.trim() || undefined,
        page: 0,
        perPage: 100,
        sortField: 'name',
        sortDirection: 'asc',
      });
      const rows = res.data?.data?.data ?? [];
      setVendors(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Failed to load vendors', err);
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => Number(v.id) === Number(selectedVendorId)) ?? null,
    [vendors, selectedVendorId]
  );

  const value = useMemo(
    () => ({
      selectedVendorId,
      selectedVendor,
      vendors,
      vendorsLoading,
      setSelectedVendorId,
      loadVendors,
      isPublic: selectedVendorId == null,
    }),
    [selectedVendorId, selectedVendor, vendors, vendorsLoading, setSelectedVendorId, loadVendors]
  );

  return <VendorContext.Provider value={value}>{children}</VendorContext.Provider>;
};

export const useVendor = () => {
  const ctx = useContext(VendorContext);
  if (!ctx) {
    throw new Error('useVendor must be used within VendorProvider');
  }
  return ctx;
};

export default VendorContext;
